import { Component, OnInit, ViewChild } from '@angular/core';
import { DesktopSidebarComponent } from "../../../../shared/components/desktop-sidebar/desktop-sidebar.component";
import { DesktopTopbarComponent } from "../../../../shared/components/desktop-topbar/desktop-topbar.component";
import { TabButtonComponent } from "../../../../shared/components/tab-button/tab-button.component";
import { EnergyChartComponent } from "../../charts/overview/energy-chart/energy-chart.component";
import { BudgetTotalCostsChartComponent } from "../../charts/overview/budget-total-costs-chart/budget-total-costs-chart.component";
import { CustomDropdownComponent } from "../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { LocationsOverviewChartComponent } from "../../charts/overview/locations-overview-chart/locations-overview-chart.component";
import { YearlyViewPerLocationChartComponent } from "../../charts/overview/yearly-view-per-location-chart/yearly-view-per-location-chart.component";
import { ElectricityForecastEoyComponent } from "../../components/overview/electricity-forecast-eoy/electricity-forecast-eoy.component";
import { GasForecastEoyComponent } from "../../components/overview/gas-forecast-eoy/gas-forecast-eoy.component";
import { MobileTopbarComponent } from "../../../../shared/components/mobile-topbar/mobile-topbar.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NavigationShareService } from '../../../../core/services/navigation-share.service';
import { WelcomeDialogComponent } from '../../dialogs/welcome-dialog/welcome-dialog.component';
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { SubscriptionService } from '../../../../core/services/subscription.service';
import { DashboardService } from '../../../../core/api/dashboard.service';
import { LocationYearlyOverviewGet, TotalEnergyConsumptionAndCostGet, YearlyOverviewOfLocationsGet } from '../../../../core/interfaces/dashboard-interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { combineLatest, Subscription } from 'rxjs';
import { Location } from '../../../../core/interfaces/location';
import { LocationService } from '../../../../core/api/location.service';
import { ActivatedRoute } from '@angular/router';
import { UserDataService } from '../../../../core/services/user-data.service';
import { LanguageService } from '../../../../core/services/language.service';
import { CompleteEnergyProfileComponent } from "../../components/overview/complete-energy-profile/complete-energy-profile.component";
import { FlexUpgradeComponent } from "../../components/flex-upgrade/flex-upgrade.component";
import { NoMembershipPopupComponent } from "../../../../shared/components/no-membership-popup/no-membership-popup.component";
import { ExtractionOngoingPopupComponent } from '../../../../shared/components/extraction-ongoing-popup/extraction-ongoing-popup.component';
import { FileExtractionService } from '../../../../core/services/file_extraction_service';

@Component({
  selector: 'app-overview',
  standalone: true,
  imports: [DesktopSidebarComponent, DesktopTopbarComponent, TabButtonComponent, EnergyChartComponent, BudgetTotalCostsChartComponent, CustomDropdownComponent, LocationsOverviewChartComponent, YearlyViewPerLocationChartComponent, ElectricityForecastEoyComponent, GasForecastEoyComponent, MobileTopbarComponent, TranslocoPipe, WelcomeDialogComponent, CustomFooterComponent, FormsModule, CalendarModule, ToastComponent, CompleteEnergyProfileComponent, FlexUpgradeComponent, NoMembershipPopupComponent, ExtractionOngoingPopupComponent],
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.css'
})
export class OverviewComponent implements OnInit {
  // Dropdown Menu
  locationsOverviewEnergyDropdownOptions: string[] = ['All Energy', 'Electricity', 'Gas'];
  selectedLocationsOverviewEnergyDropdown: number | undefined;

  locationsOverviewYtdDropdownOptions: string[] = [];
  selectedLocationsOverviewYtdDropdown: number | undefined;
  locationsOverviewYtdDropdownUpgrade: number[] = [];

  yearlyViewEnergyDropdownOptions: string[] = ['All Energy', 'Electricity', 'Gas'];
  selectedYearlyViewEnergyDropdown: number | undefined;

  yearlyViewYtdDropdownOptions: string[] = [];
  selectedYearlyViewYtdDropdown: number | undefined;
  yearlyViewYtdDropdownUpgrade: number[] = [];

  ytdTranslated: string = 'Ytd';
  last12MonthsTranslated: string = 'Last 12 months';
  next12MonthsTranslated: string = 'Next 12 months';

  currentYear = new Date().getFullYear();

  // 
  get hasPlusSubscription(): boolean {
    return this.subscriptionService.hasPlusSubscription();
  }

  get isExtractionOngoing(): boolean {
    return this.fileExtractionService.isExtractionOngoing();
  }

  get showUpgrade(): boolean {
    if (this.totalEnergyConsumptionAndCostDataDataYear < this.currentYear) {
      return false;
    }
    return this.subscriptionService.showForecastUpgradeComponents();
  }

  get showUpgradeGeneral(): boolean {
    return this.subscriptionService.showForecastUpgradeComponents();
  }

  get showDummyData(): boolean {
    return (!this.subscriptionService.hasPlusSubscription() || !this.userData.getUser()?.company?.has_files);
  }

  // Show no data dialog
  noDataDialogShow: boolean = false;
  noDataDialogShowOverview: boolean = false;

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Total energy consumption and cost
  totalEnergyConsumptionAndCostDate: Date | undefined = new Date();
  totalEnergyConsumptionAndCostDataResponse: TotalEnergyConsumptionAndCostGet | null = null;
  totalEnergyConsumptionAndCostDataLoaded: boolean = false;
  totalEnergyConsumptionAndCostDataDataYear: number = 0;
  totalEnergyConsumptionAndCostMaxYearDate: Date = new Date();
  totalEnergyConsumptionAndCostMaxYear: number = this.totalEnergyConsumptionAndCostMaxYearDate.getFullYear();

  totalCosts: number[] = new Array(12).fill(null);
  totalCostsForecastedStartMonth: number | null = null;

  gasActualData: number[] = new Array(12).fill(0);
  electricityActualData: number[] = new Array(12).fill(0);
  gasForecastData: number[] = new Array(12).fill(0);
  electricityForecastData: number[] = new Array(12).fill(0);
  gasActualConsumption: number[] = new Array(12).fill(0);
  gasForecastConsumption: number[] = new Array(12).fill(0);
  electricityActualConsumption: number[] = new Array(12).fill(0);
  electricityForecastConsumption: number[] = new Array(12).fill(0);

  consumptionValueElectricity: number = 0;
  costValueElectricity: number = 0;
  consumptionValueGas: number = 0;
  costValueGas: number = 0;

  // Overview of locations
  overviewOfLocationsDate: Date | undefined = new Date();
  overviewOfLocationsDataResponse: YearlyOverviewOfLocationsGet | null = null;
  overviewOfLocationsDataLoaded: boolean = false;
  overviewOfLocationsDataYear: number = 0;

  olLabels: string[] = ['', ''];
  olElectricityData: number[] = [0, 0];
  olGasData: number[] = [0, 0];
  @ViewChild(LocationsOverviewChartComponent) locationsOverviewChart!: LocationsOverviewChartComponent;

  // Yearly view per locations
  locationsDropdownOptions: string[] = [];
  selectedLocationDropdown: number | undefined;
  fromDate?: Date;

  listOfLocations: Location[] = [];

  yearlyViewPerLocationsDate: Date | undefined = new Date();
  yearlyViewPerLocationsDataResponse: LocationYearlyOverviewGet | null = null;
  yearlyViewPerLocationsDataLoaded: boolean = false;
  yearlyViewPerLocationsDataYear: number = 0;

  yvpl_electricityActualData = new Array(12).fill(0);
  yvpl_gasActualData = new Array(12).fill(0);
  yvpl_totalCostsData = new Array(12).fill(0);
  yvpl_electricityForecastData = new Array(12).fill(0);
  yvpl_showElectricityForecastDataset = false;
  yvpl_gasForecastData = new Array(12).fill(0);
  yvpl_showGasForecastDataset = false;
  @ViewChild(YearlyViewPerLocationChartComponent) yearlyViewPerLocationChart!: YearlyViewPerLocationChartComponent;

  // Dialog
  @ViewChild(WelcomeDialogComponent) welcomeDialog!: WelcomeDialogComponent;

  // Minimum year for year pickers
  datePickerMinYear: Date = new Date(2023, 0, 1);

  extractionCompletedSubscription?: Subscription;

  constructor(private translocoService: TranslocoService, private shareService: NavigationShareService, private subscriptionService: SubscriptionService, private dashboardService: DashboardService, private apiError: ApiErrorService, private locationService: LocationService, private route: ActivatedRoute, private userData: UserDataService, private languageService: LanguageService, private fileExtractionService: FileExtractionService) { }

  ngOnInit(): void {
    combineLatest([
      this.translocoService.selectTranslate('dashboard.electricity_and_gas'),
      this.translocoService.selectTranslate('shared.electricity'),
      this.translocoService.selectTranslate('shared.gas'),
    ]).subscribe(([allEnergyTranslation, electricityTranslation, gasTranslation]) => {
      this.locationsOverviewEnergyDropdownOptions = [
        allEnergyTranslation,
        electricityTranslation,
        gasTranslation
      ];

      this.yearlyViewEnergyDropdownOptions = [
        allEnergyTranslation,
        electricityTranslation,
        gasTranslation
      ];
    });

    // Check query params for succeded payment
    const redirectStatus = this.route.snapshot.queryParamMap.get('redirect_status') ?? null;
    if (redirectStatus === 'succeeded') {
      setTimeout(() => { this.userData.loadUserData(); }, 2000);
    }

    combineLatest([
      this.translocoService.selectTranslate('dashboard.ytd'),
      this.translocoService.selectTranslate('shared.last_12_months'),
      this.translocoService.selectTranslate('shared.next_12_months')
    ]).subscribe((
      [
        ytdLabel,
        last12MonthsLabel,
        next12MonthsLabel
      ]
    ) => {
      this.locationsOverviewYtdDropdownOptions = this.yearlyViewYtdDropdownOptions = [
        ytdLabel,
        last12MonthsLabel,
        next12MonthsLabel
      ];

      this.ytdTranslated = ytdLabel;
      this.last12MonthsTranslated = last12MonthsLabel;
      this.next12MonthsTranslated = next12MonthsLabel;
    });

    this.selectedLocationsOverviewEnergyDropdown = this.selectedLocationsOverviewYtdDropdown = this.selectedYearlyViewEnergyDropdown = this.selectedYearlyViewYtdDropdown = 0;

    // Show dialog after successful onboarding
    if (this.shareService.getState('onboarding_done') === true) {
      this.shareService.clearState('onboarding_done')
      setTimeout(() => this.openWelcomeDialog(), 1000)
    }

    // Check for language changes
    const currentLanguage = this.languageService.getCurrentUILanguage().toUpperCase();

    if (this.userData.getUser()?.settings?.ui_language != currentLanguage) {
      this.languageService.setToUserLanguage();
    }

    // Load page data
    this.refreshDashboardData()

    this.fileExtractionService.startCheckingExtractionStatus();

    this.extractionCompletedSubscription = this.fileExtractionService
      .onExtractionCompleted()
      .subscribe(() => {
        this.refreshDashboardData();
      });
  }

  refreshDashboardData(): void {
    this.loadTotalEnergyConsumptionAndCostData();
    this.loadLocationsData(); // Also loads data for charts after getting location
    this.loadOldestInvoicesDate();
  }

  ngOnDestroy(): void {
    this.extractionCompletedSubscription?.unsubscribe();
    this.fileExtractionService.stopCheckingExtractionStatus();
  }

  openWelcomeDialog(): void {
    this.welcomeDialog.openModal();
  }

  loadTotalEnergyConsumptionAndCostData() {
    if (this.totalEnergyConsumptionAndCostDate?.getFullYear() === null || this.totalEnergyConsumptionAndCostDate?.getFullYear() === undefined) {
      this.totalEnergyConsumptionAndCostDate = new Date();
    }

    this.totalEnergyConsumptionAndCostDataDataYear = this.totalEnergyConsumptionAndCostDate?.getFullYear();
    this.dashboardService.getTotalConsumptionAndCosts(this.totalEnergyConsumptionAndCostDataDataYear).subscribe({
      next: (response) => {
        if (response) {
          this.totalEnergyConsumptionAndCostDataResponse = response;

          // Populate charts
          this.populateTotalCostsChart();
          this.populateTotalConsumption();
          this.populateForecastEOY();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          if (error.error.code == 'SVC-4303') {
            showError = false;
          }
        }

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
        this.totalEnergyConsumptionAndCostDataLoaded = false;

        // Populate charts
        this.populateTotalCostsChart();
        this.populateTotalConsumption();
        this.populateForecastEOY();
      },
      complete: () => {
        // Mark api loading as completed
        this.totalEnergyConsumptionAndCostDataLoaded = true;
      }
    });
  }

  totalEnergyConsumptionAndCostYearChange() {
    this.loadTotalEnergyConsumptionAndCostData();
  }

  // Electricity costs and savings chart
  populateTotalCostsChart(): void {
    if (this.totalEnergyConsumptionAndCostDataResponse === null || this.totalEnergyConsumptionAndCostDataResponse.total_costs.length === 0) {
      this.totalCosts = new Array(12).fill(null);
      return;
    }

    this.totalCostsForecastedStartMonth = null;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    const dataYear = this.totalEnergyConsumptionAndCostDataDataYear;

    var tempCosts = new Array(12).fill(null);

    this.totalEnergyConsumptionAndCostDataResponse.total_costs.forEach(monthData => {
      const monthIndex = monthData.month - 1;

      if (dataYear < currentYear || (dataYear === currentYear && monthData.month < currentMonth)) {
        // Actual values for older months
        tempCosts[monthIndex] = monthData.actual_value ?? null;

      } else {
        // Use forecasted values for current and future months
        tempCosts[monthIndex] = monthData.forecasted_value ?? null;
        if (this.totalCostsForecastedStartMonth === null) {
          this.totalCostsForecastedStartMonth = monthIndex;
        }
      }
    });

    this.totalCosts = tempCosts;
  }

  populateTotalConsumption(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    this.gasActualData = new Array(12).fill(0);
    this.electricityActualData = new Array(12).fill(0);
    this.gasForecastData = new Array(12).fill(0);
    this.electricityForecastData = new Array(12).fill(0);
    this.gasActualConsumption = new Array(12).fill(0);
    this.gasForecastConsumption = new Array(12).fill(0);
    this.electricityActualConsumption = new Array(12).fill(0);
    this.electricityForecastConsumption = new Array(12).fill(0);

    if (!this.totalEnergyConsumptionAndCostDataResponse ||
      !this.totalEnergyConsumptionAndCostDataResponse.total_costs_electricity.length ||
      !this.totalEnergyConsumptionAndCostDataResponse.total_costs_gas.length) {
      return;
    }

    const responseYear = this.totalEnergyConsumptionAndCostDataDataYear;

    // Electricity
    this.totalEnergyConsumptionAndCostDataResponse.total_costs_electricity.forEach(monthData => {
      const monthIndex = monthData.month - 1;

      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.electricityActualData[monthIndex] = monthData.actual_value ?? 0;

      } else {
        this.electricityForecastData[monthIndex] = monthData.forecasted_value ?? 0;
      }
    });

    this.totalEnergyConsumptionAndCostDataResponse.total_consumption_electricity.forEach(monthData => {
      const monthIndex = monthData.month - 1;

      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.electricityActualConsumption[monthIndex] = monthData.actual_value ?? 0;

      } else {
        this.electricityForecastConsumption[monthIndex] = monthData.forecasted_value ?? 0;
      }
    });

    // Gas
    this.totalEnergyConsumptionAndCostDataResponse.total_costs_gas.forEach(monthData => {
      const monthIndex = monthData.month - 1;

      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.gasActualData[monthIndex] = monthData.actual_value ?? 0;
      } else {
        this.gasForecastData[monthIndex] = monthData.forecasted_value ?? 0;
      }
    });

    this.totalEnergyConsumptionAndCostDataResponse.total_consumption_gas.forEach(monthData => {
      const monthIndex = monthData.month - 1;

      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.gasActualConsumption[monthIndex] = monthData.actual_value ?? 0;

      } else {
        this.gasForecastConsumption[monthIndex] = monthData.forecasted_value ?? 0;
      }
    });

    // Show popup if no data
    this.noDataDialogShow =
      [...this.gasActualData, ...this.electricityActualData, ...this.gasForecastData, ...this.electricityForecastData]
        .every(value => value === null || value === 0);
  }

  populateForecastEOY(): void {
    this.consumptionValueElectricity = 0;
    this.costValueElectricity = 0;
    this.consumptionValueGas = 0;
    this.costValueGas = 0;

    if (!this.totalEnergyConsumptionAndCostDataResponse ||
      !this.totalEnergyConsumptionAndCostDataResponse.total_consumption_electricity.length ||
      !this.totalEnergyConsumptionAndCostDataResponse.total_consumption_gas.length ||
      !this.totalEnergyConsumptionAndCostDataResponse.total_costs.length) {
      return;
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    const responseYear = this.totalEnergyConsumptionAndCostDataDataYear;

    // Electricity
    this.totalEnergyConsumptionAndCostDataResponse.total_consumption_electricity.forEach(monthData => {
      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.consumptionValueElectricity += monthData.actual_value ?? 0;
      } else {
        this.consumptionValueElectricity += monthData.forecasted_value ?? 0;
      }
    });

    // -- Total Cost
    this.totalEnergyConsumptionAndCostDataResponse.total_costs_electricity.forEach(monthData => {
      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.costValueElectricity += monthData.actual_value ?? 0;
      } else {
        this.costValueElectricity += monthData.forecasted_value ?? 0;
      }
    });

    // Gas
    this.totalEnergyConsumptionAndCostDataResponse.total_consumption_gas.forEach(monthData => {
      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.consumptionValueGas += monthData.actual_value ?? 0;
      } else {
        this.consumptionValueGas += monthData.forecasted_value ?? 0;
      }
    });

    // -- Total Cost
    this.totalEnergyConsumptionAndCostDataResponse.total_costs_gas.forEach(monthData => {
      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.costValueGas += monthData.actual_value ?? 0;
      } else {
        this.costValueGas += monthData.forecasted_value ?? 0;
      }
    });
  }

  loadOldestInvoicesDate() {
    this.dashboardService.getOldestInvoice().subscribe({
      next: (response) => {
        if (response) {
          const oldestYear = response.year;

          // Set max year date for total consumption
          this.setMaxYearDate(oldestYear);
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          if (error.error.code == 'SVC-4303') {
            showError = false;
          }
        }

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
      }
    });
  }

  getOverviewOfLocationsDropdownYear(): number {
    if (this.selectedLocationsOverviewYtdDropdown === 1 || this.selectedLocationsOverviewYtdDropdown === undefined) {
      const currentYear = new Date().getFullYear();
      return currentYear;
    }

    return Number(this.locationsOverviewYtdDropdownOptions[this.selectedLocationsOverviewYtdDropdown]);
  }

  loadOverviewOfLocationsData(event: any) {
    if (event != 0 && event != 2) {
      this.overviewOfLocationsDataYear = this.getOverviewOfLocationsDropdownYear();

      // Check if user wants year to date
      var ytd = false;
      if (event === 0 || event === undefined) {
        ytd = true;
      }

      this.dashboardService.getLocationsPricesOverview(this.overviewOfLocationsDataYear, ytd).subscribe({
        next: (response) => {
          if (response) {
            this.overviewOfLocationsDataResponse = response;

            // Populate charts
            this.populateOverviewOfLocations();
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';
          var showError = true;

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
            if (error.error.code == 'SVC-4303') {
              showError = false;
            }
          }

          if (showError) {
            this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          }
          this.overviewOfLocationsDataLoaded = false;

          // Populate charts
          this.populateOverviewOfLocations();
        },
        complete: () => {
          // Mark api loading as completed
          this.overviewOfLocationsDataLoaded = true;
        }
      });
    }
    else {
      let from_date: Date;
      let to_date: Date;

      const today = new Date();

      if (event === 0) {
        // Last 12 months
        to_date = today;
        from_date = new Date(today);
        from_date.setFullYear(from_date.getFullYear() - 1);
      } else {
        // Next 12 months
        from_date = today;
        to_date = new Date(today);
        to_date.setFullYear(to_date.getFullYear() + 1);
      }

      this.dashboardService.getLocationsPricesOverviewPeriod(from_date, to_date).subscribe({
        next: (response) => {
          if (response) {
            this.overviewOfLocationsDataResponse = response;

            // Populate charts
            this.populateOverviewOfLocations();
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';
          var showError = true;

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
            if (error.error.code == 'SVC-4303') {
              showError = false;
            }
          }

          if (showError) {
            this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          }
          this.overviewOfLocationsDataLoaded = false;

          // Populate charts
          this.populateOverviewOfLocations();
        },
        complete: () => {
          // Mark api loading as completed
          this.overviewOfLocationsDataLoaded = true;
        }
      });
    }
  }

  populateOverviewOfLocations(): void {
    this.olLabels = [];
    this.olElectricityData = [];
    this.olGasData = [];

    if (!this.overviewOfLocationsDataResponse ||
      !this.overviewOfLocationsDataResponse.overview.length) {
      return;
    }

    // Map location ids with index
    const locationIndexMap = new Map(
      this.listOfLocations.map((loc, i) => [loc.id, i])
    );

    // Combine data for easier sorting
    const combined = this.overviewOfLocationsDataResponse.overview.map(location => {
      const index = locationIndexMap.get(location.location_id) ?? Number.MAX_SAFE_INTEGER;

      return {
        index,
        label: `Loc. ${index + 1} ${location.location_name}`,
        electricity: location.total_electricity_price ?? 0,
        gas: location.total_gas_price ?? 0
      };
    });

    // Sort by location index
    combined.sort((a, b) => a.index - b.index);

    // Fill display arrays in sorted order
    this.olLabels = combined.map(x => x.label);
    this.olElectricityData = combined.map(x => x.electricity);
    this.olGasData = combined.map(x => x.gas);

    this.noDataDialogShowOverview =
      [...this.olElectricityData, ...this.olGasData].every(value => value === 0 || value === null);

    setTimeout(() => {
      this.locationsOverviewEnergyFilter();
    }, 100);
  }

  locationsOverviewEnergyFilter(): void {
    const option = this.selectedLocationsOverviewEnergyDropdown;
    if (option === 0) // All energy
    {
      this.locationsOverviewChart.toggleDataset(0, false);
      this.locationsOverviewChart.toggleDataset(1, false);
    }
    else if (option === 1) // Electricity
    {
      this.locationsOverviewChart.toggleDataset(0, false);
      this.locationsOverviewChart.toggleDataset(1, true);
    }
    else if (option === 2) // Gas
    {
      this.locationsOverviewChart.toggleDataset(0, true);
      this.locationsOverviewChart.toggleDataset(1, false);
    }
  }

  populateLocationsDropdown() {
    this.locationsDropdownOptions = [];

    const currentSelection = this.selectedLocationDropdown;

    for (let i = 0; i < this.listOfLocations.length; i++) {
      const location = this.listOfLocations[i];
      const locationString = `${location.street ?? ''} ${location.number ?? ''}, ${location.postal_code ?? ''} ${location.city ?? ''}`;
      this.locationsDropdownOptions.push(locationString);
    }

    if (this.selectedLocationDropdown === undefined) {
      this.selectedLocationDropdown = (this.listOfLocations.length === 0) ? undefined : 0;
    } else {
      if (currentSelection !== undefined && currentSelection >= this.listOfLocations.length) {
        this.selectedLocationDropdown = (this.listOfLocations.length === 0) ? undefined : 0;
      }
    }
  }

  loadLocationsData() {
    this.locationService.getLocations().subscribe({
      next: (response) => {
        if (response) {
          this.listOfLocations = response;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
        this.populateLocationsDropdown();

        // Load chart data
        this.loadYearlyViewPerLocationsData(0);

        // Load overview of location
        this.loadOverviewOfLocationsData(0);
      }
    });
  }

  getYearlyViewOfLocationsDropdownYear(): number {
    if (this.selectedYearlyViewYtdDropdown === 1 || this.selectedYearlyViewYtdDropdown === undefined) {
      const currentYear = new Date().getFullYear();
      return currentYear;
    }

    return Number(this.locationsOverviewYtdDropdownOptions[this.selectedYearlyViewYtdDropdown]);
  }

  loadYearlyViewPerLocationsData(event: any, changeSelect: boolean = false) {

    if (this.selectedLocationDropdown === undefined) { return; }
    this.yearlyViewPerLocationsDataYear = this.getYearlyViewOfLocationsDropdownYear();

    if (changeSelect) { this.selectedYearlyViewYtdDropdown = event; }

    if (this.selectedYearlyViewYtdDropdown != 0 && this.selectedYearlyViewYtdDropdown != 2) {
      // Check if user wants year to date
      var ytd = false;
      if (this.selectedYearlyViewYtdDropdown === 1 || this.selectedYearlyViewYtdDropdown === undefined) {
        ytd = true;
      }

      // Show or hide forecast
      if (ytd) {
        this.yvpl_showElectricityForecastDataset = false;
        this.yvpl_showGasForecastDataset = false;
      }
      else {
        this.yvpl_showElectricityForecastDataset = true;
        this.yvpl_showGasForecastDataset = true;
      }

      this.fromDate = undefined;

      this.dashboardService.getLocationOverview(this.listOfLocations[this.selectedLocationDropdown].id, this.yearlyViewPerLocationsDataYear, ytd).subscribe({
        next: (response) => {
          if (response) {
            this.yearlyViewPerLocationsDataResponse = response;

            // Populate charts
            this.populateYearlyViewPerLocation()
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';
          var showError = true;

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
            if (error.error.code == 'SVC-4303') {
              showError = false;
            }
          }

          if (showError) {
            this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          }
          this.yearlyViewPerLocationsDataLoaded = false;

          // Populate charts
          this.populateYearlyViewPerLocation()
        },
        complete: () => {
          // Mark api loading as completed
          this.yearlyViewPerLocationsDataLoaded = true;
        }
      });
    }
    else {
      let from_date: Date;
      let to_date: Date;

      const today = new Date();

      if (this.selectedYearlyViewYtdDropdown === 0) {
        // Last 12 months
        to_date = today;
        from_date = new Date(today);
        from_date.setFullYear(from_date.getFullYear() - 1);

        this.yvpl_showElectricityForecastDataset = false;
        this.yvpl_showGasForecastDataset = false;
      } else {
        // Next 12 months
        from_date = today;
        to_date = new Date(today);
        to_date.setFullYear(to_date.getFullYear() + 1);

        this.yvpl_showElectricityForecastDataset = true;
        this.yvpl_showGasForecastDataset = true;
      }

      this.fromDate = from_date;

      this.dashboardService.getLocationPeriodOverview(this.listOfLocations[this.selectedLocationDropdown].id, from_date, to_date).subscribe({
        next: (response) => {
          if (response) {
            this.yearlyViewPerLocationsDataResponse = response;

            // Populate charts
            this.populatePeriodViewPerLocation()
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';
          var showError = true;

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
            if (error.error.code == 'SVC-4303') {
              showError = false;
            }
          }

          if (showError) {
            this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          }
          this.yearlyViewPerLocationsDataLoaded = false;

          // Populate charts
          this.populatePeriodViewPerLocation()
        },
        complete: () => {
          // Mark api loading as completed
          this.yearlyViewPerLocationsDataLoaded = true;
        }
      });
    }
  }

  populateYearlyViewPerLocation(): void {
    this.yvpl_electricityActualData = new Array(12).fill(0);
    this.yvpl_gasActualData = new Array(12).fill(0);
    this.yvpl_totalCostsData = new Array(12).fill(0);
    this.yvpl_electricityForecastData = new Array(12).fill(0);
    this.yvpl_gasForecastData = new Array(12).fill(0);

    if (!this.yearlyViewPerLocationsDataResponse ||
      !this.yearlyViewPerLocationsDataResponse.total_consumption_electricity.length ||
      !this.yearlyViewPerLocationsDataResponse.total_consumption_gas.length ||
      !this.yearlyViewPerLocationsDataResponse.total_costs.length) {
      return;
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    const responseYear = this.yearlyViewPerLocationsDataYear;

    // Electricity
    this.yearlyViewPerLocationsDataResponse.total_consumption_electricity.forEach(monthData => {
      const monthIndex = monthData.month - 1;

      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.yvpl_electricityActualData[monthIndex] = monthData.actual_value ?? 0;
      } else {
        this.yvpl_electricityForecastData[monthIndex] = monthData.forecasted_value ?? 0;
      }
    });

    // Gas
    this.yearlyViewPerLocationsDataResponse.total_consumption_gas.forEach(monthData => {
      const monthIndex = monthData.month - 1;

      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.yvpl_gasActualData[monthIndex] = monthData.actual_value ?? 0;
      } else {
        this.yvpl_gasForecastData[monthIndex] = monthData.forecasted_value ?? 0;
      }
    });

    // Total Costs
    this.yearlyViewPerLocationsDataResponse.total_costs.forEach(monthData => {
      const monthIndex = monthData.month - 1;

      if (responseYear < currentYear || (responseYear === currentYear && monthData.month < currentMonth)) {
        this.yvpl_totalCostsData[monthIndex] = monthData.actual_value ?? 0;
      } else {
        this.yvpl_totalCostsData[monthIndex] = monthData.forecasted_value ?? 0;
      }
    });

    // Reset filter
    setTimeout(() => {
      this.locationsYearlyViewPerLocationEnergyFilter();
    }, 100);
  }

  populatePeriodViewPerLocation(): void {
    const res = this.yearlyViewPerLocationsDataResponse;
    if (!res?.total_consumption_electricity?.length || !res?.total_consumption_gas?.length || !res?.total_costs?.length) {
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    var _fromDate = this.fromDate!;
    _fromDate.setHours(0, 0, 0, 0);

    const isAllForecast = _fromDate >= today;

    const monthsCount = res.total_consumption_electricity.length;

    this.yvpl_electricityActualData = new Array(monthsCount).fill(0);
    this.yvpl_electricityForecastData = new Array(monthsCount).fill(0);
    this.yvpl_gasActualData = new Array(monthsCount).fill(0);
    this.yvpl_gasForecastData = new Array(monthsCount).fill(0);
    this.yvpl_totalCostsData = new Array(monthsCount).fill(0);

    for (let i = 0; i < monthsCount; i++) {
      const electricity = res.total_consumption_electricity[i];
      const gas = res.total_consumption_gas[i];
      const costs = res.total_costs[i];

      if (isAllForecast) {
        this.yvpl_electricityForecastData[i] = electricity.forecasted_value ?? 0;
        this.yvpl_gasForecastData[i] = gas.forecasted_value ?? 0;
        this.yvpl_totalCostsData[i] = costs.forecasted_value ?? 0;
      } else {
        this.yvpl_electricityActualData[i] = electricity.actual_value ?? 0;
        this.yvpl_gasActualData[i] = gas.actual_value ?? 0;
        this.yvpl_totalCostsData[i] = costs.actual_value ?? 0;
      }
    }

    // Reset filter
    setTimeout(() => {
      this.locationsYearlyViewPerLocationEnergyFilter();
    }, 100);
  }

  locationsYearlyViewPerLocationEnergyFilter(): void {
    const option = this.selectedYearlyViewEnergyDropdown;
    if (option === 0) // All energy
    {
      this.yearlyViewPerLocationChart.toggleDataset(0, false);
      this.yearlyViewPerLocationChart.toggleDataset(1, false);
      this.yearlyViewPerLocationChart.toggleDataset(2, false);
      this.yearlyViewPerLocationChart.toggleDataset(3, false);
    }
    else if (option === 1) // Electricity
    {
      this.yearlyViewPerLocationChart.toggleDataset(0, false);
      this.yearlyViewPerLocationChart.toggleDataset(1, true);
      this.yearlyViewPerLocationChart.toggleDataset(2, false);
      this.yearlyViewPerLocationChart.toggleDataset(3, true);
    }
    else if (option === 2) // Gas
    {
      this.yearlyViewPerLocationChart.toggleDataset(0, true);
      this.yearlyViewPerLocationChart.toggleDataset(1, false);
      this.yearlyViewPerLocationChart.toggleDataset(2, true);
      this.yearlyViewPerLocationChart.toggleDataset(3, false);
    }
  }

  setMaxYearDate(oldestYear: number) {
    this.dashboardService.getLatestForecastInvoice().subscribe({
      next: (response) => {
        if (!this.showUpgradeGeneral) {

          this.totalEnergyConsumptionAndCostMaxYearDate.setFullYear(
            response.year
          );
          this.totalEnergyConsumptionAndCostMaxYear = this.totalEnergyConsumptionAndCostMaxYearDate.getFullYear();
        }

        this.populateDropdownOptions(oldestYear, response.year);
      },
      error: async (error) => {
        if (!this.showUpgradeGeneral) {
          this.totalEnergyConsumptionAndCostMaxYearDate.setFullYear(
            this.totalEnergyConsumptionAndCostMaxYearDate.getFullYear() + 1
          );
          this.totalEnergyConsumptionAndCostMaxYear = this.totalEnergyConsumptionAndCostMaxYearDate.getFullYear();
        }

        this.populateDropdownOptions(oldestYear, this.totalEnergyConsumptionAndCostMaxYear);
      }
    });
  }

  populateDropdownOptions(oldestYear: number, latestYear: number) {
    this.locationsOverviewYtdDropdownOptions = [];
    this.yearlyViewYtdDropdownOptions = [];

    if (this.selectedLocationsOverviewYtdDropdown === undefined) {
      this.selectedLocationsOverviewYtdDropdown = 0;
    }

    if (this.selectedYearlyViewYtdDropdown === undefined) {
      this.selectedYearlyViewYtdDropdown = 0;
    }

    this.locationsOverviewYtdDropdownOptions.push(this.last12MonthsTranslated, this.ytdTranslated, this.next12MonthsTranslated);
    this.yearlyViewYtdDropdownOptions.push(this.last12MonthsTranslated, this.ytdTranslated, this.next12MonthsTranslated);
    for (let i = oldestYear, index = 1; i <= latestYear; i++, index++) {
      this.locationsOverviewYtdDropdownOptions.push(i.toString());
      this.yearlyViewYtdDropdownOptions.push(i.toString());

      if ((i >= this.currentYear || index == 2) && this.showUpgradeGeneral) {
        this.locationsOverviewYtdDropdownUpgrade.push(index);
        this.yearlyViewYtdDropdownUpgrade.push(index);
      }
    }

  }
}
